import { useState, useEffect, useContext } from 'react';
import {
  Select,
  TextInput,
  Stack,
  Text,
  Modal,
  Button,
  Group
} from '@mantine/core';
import { ChatContext } from '../context/ChatContext';
import { useMantineTheme } from '@mantine/core';

const providers = [
    { value: 'hidemium', label: 'Hidemium Free' },
    { value: 'anthropic', label: 'Anthropic' },
    { value: 'gemini', label: 'Gemini' },
    { value: 'openai', label: 'OpenAI' },
];

const geminiModels = [
    { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
    { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' },
    { value: 'gemini-2.0-flash-lite', label: 'Gemini 2.0 Flash-Lite' },
    { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' },
    { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
];

const openaiModels = [
    { value: 'gpt-4.1', label: 'GPT-4.1 (2025)' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
    { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano' },
    { value: 'gpt-4', label: 'GPT-4 (Legacy)' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo (Current)' },
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo (Current)' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo 16K (Legacy)' },
];

const claudeModels = [
    { value: 'claude-opus-4-20250514', label: 'Claude Opus 4' },
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
    { value: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet' },
    { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
    { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku' },
    { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
    { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
];

const ConfigModel = () => {
  const { modelConfig, updateModelConfig } = useContext(ChatContext);
  const [provider, setProvider] = useState(modelConfig.provider || 'hidemium');
  const [model, setModel] = useState(modelConfig.model || '');
  const [apiKey, setApiKey] = useState(modelConfig.apiKey || '');
  const [opened, setOpened] = useState(false);
  const theme = useMantineTheme();
  const isTet = theme.colorScheme === 'tet';
  
  const getModelOptions = () => {
    switch (provider) {
      case 'gemini':
        return geminiModels;
      case 'openai':
        return openaiModels;
      case 'anthropic':
        return claudeModels;
      default:
        return [];
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setProvider(modelConfig.provider || 'hidemium');
      setModel(modelConfig.model || '');
      setApiKey(modelConfig.apiKey || '');
    }, [])
  }, [modelConfig, opened]);

  useEffect(() => {
    if (opened) {
      if (provider === 'gemini') {
        setModel('gemini-2.5-pro-exp-03-25');
      } else if (provider === 'openai') {
        setModel('gpt-4');
      } else if (provider === 'anthropic') {
        setModel('claude-3-7-sonnet-20250219');
      } else {
        setModel('');
      }
    }
  }, [provider, opened]);

  // Sync local state with context when modal opens
  useEffect(() => {
    if (opened) {
      setProvider(modelConfig.provider || 'hidemium');
      setModel(modelConfig.model || '');
      setApiKey(modelConfig.apiKey || '');
    }
  }, [opened, modelConfig]);

  const handleSave = () => {
    if (provider !== 'hidemium' && (!model || !apiKey)) {
      return; // Don't save if required fields are missing
    }

    // Save to context
    updateModelConfig({
      provider,
      model: provider === 'hidemium' ? null : model,
      apiKey: provider === 'hidemium' ? null : apiKey
    });

    // Close modal
    setOpened(false);
  };

  return (
    <>
      <Button
        variant="gradient"
        
        gradient={
          isTet 
            ? { from: '#d32f2f', to: '#fbc02d', deg: 90 } 
            : { from: 'indigo', to: 'cyan' }
        }
        
        size="xs"
        onClick={() => setOpened(true)}
      >
        Config Model
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Model Configuration"
        size="md"
        zIndex={9999}
      >
        <Stack spacing="md">
          <Select
            label="Provider"
            placeholder="Select provider"
            data={providers}
            value={provider}
            onChange={setProvider}
            required
          />

          {provider !== 'hidemium' && (
            <>
              <Select
                label="Model"
                placeholder="Select model"
                data={getModelOptions()}
                value={model}
                onChange={setModel}
                required
              />

              <TextInput
                label="API Key"
                placeholder="Enter your API key"
                value={apiKey}
                onChange={(event) => setApiKey(event.currentTarget.value)}
                onKeyDown={(event) => event.stopPropagation()}
                required
              />

              {provider === 'gemini' && (
                <Text size="xs" color="dimmed">
                  <a href="https://aistudio.google.com/apikey" target='_blank'>
                    You can get your Gemini API key from Google AI Studio.
                  </a>
                </Text>
              )}

              {provider === 'openai' && (
                <Text size="xs" color="dimmed">
                  <a href="https://platform.openai.com/api-keys" target='_blank'>
                    You can get your OpenAI API key from your OpenAI account.
                  </a>
                </Text>
              )}

              {provider === 'anthropic' && (
                <Text size="xs" color="dimmed">
                  <a target='_blank' href="https://console.anthropic.com/settings/keys">
                    You can get your Anthropic API key from your Anthropic account.
                  </a>
                </Text>
              )}
            </>
          )}

          {provider === 'hidemium' && (
            <p>
                <Text size="sm">
                    For better results, it's recommended to use our model instead.
                </Text>
                <Text size="sm">
                    Hidemium uses the free Gemini model, which comes with some limitations.
                </Text>
            </p>
          )}

          <Group position="right" mt="md">
            <Button variant={isTet ? 'outline' : 'outline'} onClick={() => setOpened(false)}>Cancel</Button>
            <Button variant={isTet ? 'outline' : 'outline'} onClick={handleSave}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default ConfigModel;